/**
=========================================================
* Material Dashboard 2  React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import PropTypes from "prop-types";
import { useMaterialUIController } from "context";


// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

ChartJS.register(ArcElement, Tooltip, Legend);

function PieChart({
  icon = { color: "info", component: "" },
  title = "",
  description = "",
  height = "19.125rem",
  chart,
}) {
  // Si no hay datos, no renderizar el gráfico
  if (!chart || !Array.isArray(chart.labels) || !Array.isArray(chart.datasets) || chart.labels.length === 0) {
    return (
      <MDBox height={height} display="flex" alignItems="center" justifyContent="center">
        <MDTypography color="text">No hay datos para graficar.</MDTypography>
      </MDBox>
    );
  }

  const data = {
    labels: chart.labels,
    datasets: chart.datasets,
  };

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const legendColor = darkMode ? "#fff" : "#333";
  const options = {
    responsive: true,
    plugins: {
      legend: { position: "right", labels: { color: legendColor } },
    },
  };

  const renderChart = (
    <MDBox py={2} pr={2} pl={icon.component ? 1 : 2}>
      {title || description ? (
        <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
          {icon.component && (
            <MDBox
              width="4rem"
              height="4rem"
              bgColor={icon.color || "dark"}
              variant="gradient"
              coloredShadow={icon.color || "dark"}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-5}
              mr={2}
            >
              <Icon fontSize="medium">{icon.component}</Icon>
            </MDBox>
          )}
          <MDBox mt={icon.component ? -2 : 0}>
            {title && <MDTypography variant="h6">{title}</MDTypography>}
            <MDBox mb={2}>
              <MDTypography component="div" variant="button" color="text">
                {description}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}
      <MDBox height={height}>
        <Pie data={data} options={options} />
      </MDBox>
    </MDBox>
  );

  if (title || description) {
    return (
      <Card>
        <MDBox py={2} pr={2} pl={icon.component ? 1 : 2}>
          {title || description ? (
            <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
              {icon.component && (
                <MDBox
                  width="4rem"
                  height="4rem"
                  bgColor={icon.color || "dark"}
                  variant="gradient"
                  coloredShadow={icon.color || "dark"}
                  borderRadius="xl"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  color="white"
                  mt={-5}
                  mr={2}
                >
                  <Icon fontSize="medium">{icon.component}</Icon>
                </MDBox>
              )}
              <MDBox mt={icon.component ? -2 : 0}>
                {title && <MDTypography variant="h6">{title}</MDTypography>}
                <MDBox mb={2}>
                  <MDTypography component="div" variant="button" color="text">
                    {description}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          ) : null}
          <MDBox height={height} width="100%" sx={{ overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Pie data={data} options={options} style={{ maxHeight: height, maxWidth: "100%" }} />
          </MDBox>
        </MDBox>
      </Card>
    );
  }
  return renderChart;
}

PieChart.propTypes = {
  icon: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
    ]),
    component: PropTypes.node,
  }),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
};

export default PieChart;
